import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';

import {
  ENTITIES,
  TASK_NOTIFICATION_KINDS,
  TASK_NOTIFICATION_TEXTS,
} from '../constants';
import { getManagerByIdSelector } from '../../../../store/selectors/tickets';
import Avatar from '@components/CRM/Clients/Avatar';
import { Eye, Remove } from '@icons/crm';
import Bubble from '@components/Bubble';
import { ValueOf } from '@utils/tsUtils';
import { INotification } from 'types/types';
import { Bot } from '@icons/communication';

interface INotificationProps {
  notification: INotification;
  onReadNotifications: (params: {
    id?: string;
    markAll?: boolean;
    onSuccess?: () => void;
  }) => void;
  onDeleteNotifications: (params: {
    id?: string;
    markAll?: boolean;
    onSuccess: () => void;
  }) => void;
  goToNotificationEntity: (params: {
    entityType: ValueOf<typeof ENTITIES>;
    id: string;
  }) => void;
  setIsOpenDropdown: (isOpen: boolean) => void;
}

const ManagerLabel = ({ managerId }: { managerId: string }) => {
  const manager = useSelector(getManagerByIdSelector(managerId));

  if (!manager) return null;

  return (
    <StyledCenteredFlexWrapper>
      <StyledAvatar url={manager.avatarUrl} />
      <StyledName>{manager.name}</StyledName>
    </StyledCenteredFlexWrapper>
  );
};

const Notification = ({
  notification,
  onReadNotifications,
  onDeleteNotifications,
  goToNotificationEntity,
  setIsOpenDropdown,
}: INotificationProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    kind,
    managerId,
    entityId,
    entityType,
    summary,
    createdAt,
    readAt,
    id,
  } = notification;

  const handleClick = () => {
    goToNotificationEntity({ entityType, id: entityId });
    onReadNotifications({ id });
    setIsOpenDropdown(false);
  };

  const handleRead = () => {
    setIsLoading(true);
    onReadNotifications({ id, onSuccess: () => setIsLoading(false) });
  };

  const handleDelete = () => {
    setIsLoading(true);
    onDeleteNotifications({ id, onSuccess: () => setIsLoading(false) });
  };

  const getNotificationByKind = (entityType, kind) => {
    // TODO: Тут пока только уведомления по задачам, по тикетам добавить позже
    if (entityType !== ENTITIES.TASK) return null;

    switch (kind) {
      case TASK_NOTIFICATION_KINDS.DEADLINE_APPROACHING:
      case TASK_NOTIFICATION_KINDS.REMINDER:
        return (
          <StyledCenteredFlexWrapper>
            <StyledBot width={30} />
            <span>{TASK_NOTIFICATION_TEXTS[kind]}</span>
          </StyledCenteredFlexWrapper>
        );

      default:
        return (
          <>
            <ManagerLabel managerId={managerId} />
            <span>{TASK_NOTIFICATION_TEXTS[kind]}</span>
          </>
        );
    }
  };

  return (
    <StyledWrapper
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={handleClick}
      $isHighlighted={!readAt}
    >
      {isLoading && <StyledOverlay />}
      <StyledHeader>{getNotificationByKind(entityType, kind)}</StyledHeader>
      <StyledContent>
        <StyledSummary>{`#${entityId} ${summary}`}</StyledSummary>
        <StyledDate>
          {format(new Date(createdAt), 'dd MMM yyyy, HH:mm', {
            locale: ru,
          })}
        </StyledDate>
      </StyledContent>

      {isHovered && (
        <StyledButtonsWrapper>
          {!readAt && (
            <StyledBubble placement="top" text="Отметить прочитанным">
              <StyledButton onClick={handleRead}>
                <Eye width={15} />
              </StyledButton>
            </StyledBubble>
          )}
          <StyledBubble placement="top" text="Удалить уведомление">
            <StyledButton onClick={handleDelete}>
              <Remove width={15} />
            </StyledButton>
          </StyledBubble>
        </StyledButtonsWrapper>
      )}
    </StyledWrapper>
  );
};

const StyledBot = styled(Bot)`
  & * {
    stroke-width: 0.5 !important;
  }
`;

const StyledOverlay = styled.div`
  width: 100%;
  height: 100%;
  background: #dfdfdf;
  position: absolute;
  top: 0;
  left: 0;
  border-radius: 4px;
  z-index: 1;
  opacity: 0.5;
`;

const StyledBubble = styled(Bubble)`
  & > div {
    display: flex;
  }
`;

const StyledButton = styled.button`
  border: none;
  outline: none;
  background: transparent;
  cursor: pointer;
  border-radius: 2px;
`;

const StyledButtonsWrapper = styled.div`
  position: absolute;
  top: 16px;
  right: 8px;
  display: flex;
  gap: 8px;
`;

const StyledContent = styled.div`
  margin-left: 36px;
`;

const StyledHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-right: 48px;
  font-weight: bold;
`;

const StyledDate = styled.div`
  margin-top: 6px;
  font-size: 12px;
  opacity: 0.6;
  text-align: left;
`;

const StyledSummary = styled.div`
  font-size: 14px;
  text-align: left;
`;

const StyledWrapper = styled.div<{ $isHighlighted: boolean }>`
  cursor: pointer;
  border-radius: 4px;
  padding: 8px;
  position: relative;
  height: 90px;
  display: flex;
  flex-direction: column;
  justify-content: space-around;

  ${({ $isHighlighted }) =>
    $isHighlighted &&
    css`
      background: rgba(0, 123, 255, 0.15);
    `}

  &:hover {
    background: #eaeaea;
  }
`;

const StyledCenteredFlexWrapper = styled.div`
  display: flex;
  gap: 5px;
  align-items: center;
`;

const StyledName = styled.div`
  font-weight: 600;
  font-size: 14px;
`;

const StyledAvatar = styled(Avatar)`
  width: 30px;
  height: 30px;
`;

export default Notification;
